import type {
  AuditActorKind,
  AuditSubjectType,
  CustomerProfileAuditField,
  EventAuditField,
  HistoryKind,
  ReservationAuditField,
} from './kinds'

/**
 * The audit log as a premise sees it: the `premise` kinds of the catalogue, each with only the fields its presenter
 * passes on (`history.presenter.ts`). Internal kinds, the request id and the raw ids never reach this shape.
 */

/** Who made the change. `name` is null for a public form and the system, and when the person is gone. */
export interface HistoryActor {
  kind: AuditActorKind
  name: string | null
}

/** A value as shown: ids are already names, an instant is an ISO string. */
export type HistoryValue = string | number | boolean | null | readonly string[]

/** `null` for a field whose value is not kept (a description): it changed, and that is all that is known. */
export type HistoryChanges<F extends string> = Partial<Record<F, { from: HistoryValue; to: HistoryValue } | null>>

export interface HistoryDataMap {
  /** `fromSeries`: the edit reached this termín from "Tento a nasledujúce". */
  'event.edited': { changes: HistoryChanges<EventAuditField>; fromSeries: boolean }
  'event.cancelled': { reason: string | null; participations: number }
  'event.addedToSeries': { seriesName: string | null }
  'participation.cancelled': { reason: string | null; withEvent: boolean }
  'participation.reactivated': { waiting: boolean }
  'participation.promoted': Record<string, never>
  'participation.removed': { cause: 'staff' | 'customerDeleted' | 'groupLeft' }
  'participation.attendanceMarked': { attended: boolean | null }
  'reservation.edited': { changes: HistoryChanges<ReservationAuditField> }
  'reservation.cancelled': { reason: string | null }
  'customer.blocked': { message: string | null }
  'customer.unblocked': { message: string | null }
  'customer.cardChanged': { from: string | null; to: string | null }
  'customer.pointsChanged': { from: number; to: number }
  'customer.edited': { fields: CustomerProfileAuditField[] }
  'customer.consentGranted': { via: 'reservation' | 'event' }
}

interface HistoryEntryOf<K extends HistoryKind> {
  id: string
  kind: K
  at: Date
  subjectType: AuditSubjectType
  subjectId: string
  actor: HistoryActor
  /** The client the row is about, as the premise keeps them; null for a row about no client, or one gone. */
  customer: string | null
  data: HistoryDataMap[K]
}

export type HistoryEntry = { [K in HistoryKind]: HistoryEntryOf<K> }[HistoryKind]

export interface GetHistoryResponse {
  /** Newest first. */
  entries: HistoryEntry[]
  /**
   * The first audit row of each subject the response covers, internal kinds included. From then on the log is the
   * authority for that subject, and a stamp holding only the latest value (`updatedAt`, `cancelledAt`) is not shown
   * as well (`legacyStampShown`, docs/audit.md).
   */
  auditedSince: Record<string, Date>
}

export const EMPTY_HISTORY: GetHistoryResponse = { entries: [], auditedSince: {} }
